import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Clock, AlertCircle, FileText, Copy, Check, Loader2, Hourglass } from 'lucide-react';
import type { AcordoPublico, ParcelaPublica, SituacaoParcelaPublica } from '../../supabase/functions/_shared/acordoPublico';

// Página pública do acordo de parcelamento (#6204), aberta pelo link que o associado
// recebe. Sem login: quem protege é o código no link (ver edge function acordo-publico).

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (iso: string | null) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '—');

const SITUACAO: Record<SituacaoParcelaPublica, { rotulo: string; classe: string; icone: React.ReactNode }> = {
    paga: { rotulo: 'Paga', classe: 'bg-emerald-50 text-emerald-700 border-emerald-200', icone: <CheckCircle2 size={14} /> },
    em_aberto: { rotulo: 'Em aberto', classe: 'bg-sky-50 text-sky-700 border-sky-200', icone: <Clock size={14} /> },
    vencida: { rotulo: 'Vencida', classe: 'bg-red-50 text-red-700 border-red-200', icone: <AlertCircle size={14} /> },
    a_emitir: { rotulo: 'Boleto ainda não emitido', classe: 'bg-gray-50 text-gray-500 border-gray-200', icone: <Hourglass size={14} /> },
    cancelada: { rotulo: 'Cancelada', classe: 'bg-gray-50 text-gray-500 border-gray-200', icone: <AlertCircle size={14} /> },
};

// O navegador interno do WhatsApp (WebView) muitas vezes não tem navigator.clipboard.
// Sem plano B o botão ficava mudo: tenta o clipboard, depois execCommand.
const copiarTexto = async (texto: string): Promise<boolean> => {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(texto);
            return true;
        }
    } catch { /* cai no plano B */ }
    try {
        const area = document.createElement('textarea');
        area.value = texto;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
    } catch {
        return false;
    }
};

// `contexto` ("da Parcela 2") só para leitor de tela: a página tem vários botões "Copiar PIX" iguais.
const BotaoCopiar: React.FC<{ texto: string; rotulo: string; contexto: string }> = ({ texto, rotulo, contexto }) => {
    const [copiado, setCopiado] = useState(false);
    const [falhou, setFalhou] = useState(false);
    return (
        <>
            <button
                type="button"
                onClick={async () => {
                    const ok = await copiarTexto(texto);
                    setFalhou(!ok);
                    if (ok) {
                        setCopiado(true);
                        setTimeout(() => setCopiado(false), 2000);
                    }
                }}
                className="inline-flex min-h-[44px] flex-1 sm:flex-none items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 active:bg-gray-100"
            >
                {copiado ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                <span aria-live="polite">{copiado ? 'Copiado' : rotulo}</span>
                <span className="sr-only"> {contexto}</span>
            </button>
            {falhou ? (
                <div className="basis-full space-y-1.5">
                    <p className="text-xs text-gray-600">Não deu para copiar sozinho. Toque no campo abaixo, segure e escolha “Copiar”.</p>
                    <input
                        readOnly
                        autoFocus
                        value={texto}
                        aria-label={`${rotulo} ${contexto}`}
                        onFocus={e => e.currentTarget.select()}
                        className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-800"
                    />
                </div>
            ) : null}
        </>
    );
};

const LinhaParcela: React.FC<{ p: ParcelaPublica; encerrado: boolean }> = ({ p, encerrado }) => {
    // Acordo encerrado não oferece boleto: parcela não paga como "Em aberto"/"Vencida" sem
    // botão para pagar confundiria. Paga continua paga.
    const situacao = encerrado && p.situacao !== 'paga' ? 'cancelada' : p.situacao;
    const s = SITUACAO[situacao];
    const nome = p.tipo === 'entrada' ? 'Entrada' : `Parcela ${p.numero}`;
    return (
        <li className={`rounded-xl border border-gray-100 bg-white p-4 shadow-sm ${situacao === 'cancelada' ? 'opacity-70' : ''}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-base font-semibold text-gray-900">{nome}</p>
                    <p className="text-sm text-gray-500">Vence em {dataBr(p.vencimento)}</p>
                </div>
                <p className="shrink-0 text-base font-bold text-gray-900">{brl(p.valor)}</p>
            </div>
            {/* Situação em linha própria: "Boleto ainda não emitido" é longo e quebrava a pílula ao lado do valor em telas de 360px. */}
            <span className={`mt-2.5 inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${s.classe}`}>
                {s.icone}{s.rotulo}
            </span>
            {p.boleto_url ? (
                <div className="mt-3 flex flex-wrap gap-2">
                    <a
                        href={p.boleto_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-[44px] w-full sm:w-auto items-center justify-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 active:bg-primary-dark"
                    >
                        <FileText size={16} /> Ver boleto<span className="sr-only"> da {nome} (abre em nova aba)</span>
                    </a>
                    {p.pix ? <BotaoCopiar texto={p.pix} rotulo="Copiar PIX" contexto={`da ${nome}`} /> : null}
                    {p.linha_digitavel ? <BotaoCopiar texto={p.linha_digitavel} rotulo="Copiar código de barras" contexto={`da ${nome}`} /> : null}
                </div>
            ) : null}
        </li>
    );
};

const AcordoPublicoView: React.FC = () => {
    const { token } = useParams();
    const [acordo, setAcordo] = useState<AcordoPublico | null>(null);
    // 'falha' = não deu para carregar agora (sem internet, servidor fora); 'indisponivel' = o link não existe.
    const [estado, setEstado] = useState<'carregando' | 'ok' | 'indisponivel' | 'falha'>('carregando');
    const [tentativa, setTentativa] = useState(0);

    useEffect(() => {
        document.title = 'Meu acordo · Vigor Energy';
        let cancelado = false;
        setEstado('carregando');
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/acordo-publico?t=${encodeURIComponent(token || '')}`;
        const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
        fetch(url, { headers: { apikey: chave, Authorization: `Bearer ${chave}` } })
            .then(async r => {
                if (cancelado) return;
                if (r.status === 404) { setEstado('indisponivel'); return; }
                if (!r.ok) throw new Error(String(r.status));
                setAcordo(await r.json());
                setEstado('ok');
            })
            .catch(() => { if (!cancelado) setEstado('falha'); });
        return () => { cancelado = true; };
    }, [token, tentativa]);

    const pagas = acordo?.parcelas.filter(p => p.situacao === 'paga').length ?? 0;

    return (
        <div className="min-h-screen bg-background">
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-xl mx-auto px-4 h-16 flex items-center">
                    {/* Mesmo logo do Portal do Consumidor (public/LOGO-01.png, h-10 sm:h-12 como no cabeçalho de lá). */}
                    <img src="/LOGO-01.png" alt="Vigor Energy" className="h-10 sm:h-12 w-auto object-contain" />
                </div>
            </header>
            <main className="max-w-xl mx-auto px-4 py-6 space-y-5">
                {estado === 'carregando' ? (
                    <div role="status" className="flex justify-center py-20">
                        <Loader2 size={28} className="animate-spin text-primary" />
                        <span className="sr-only">Carregando seu acordo</span>
                    </div>
                ) : estado === 'falha' ? (
                    <div role="alert" className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
                        <AlertCircle size={28} className="mx-auto text-gray-400" />
                        <p className="mt-3 font-semibold text-gray-900">Não conseguimos abrir seu acordo agora</p>
                        <p className="mt-1 text-sm text-gray-500">Verifique sua conexão e tente de novo. Seu link continua válido.</p>
                        <button
                            type="button"
                            onClick={() => setTentativa(t => t + 1)}
                            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 active:bg-primary-dark"
                        >
                            Tentar de novo
                        </button>
                    </div>
                ) : estado === 'indisponivel' || !acordo ? (
                    <div role="alert" className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-sm">
                        <AlertCircle size={28} className="mx-auto text-gray-400" />
                        <p className="mt-3 font-semibold text-gray-900">Acordo indisponível</p>
                        <p className="mt-1 text-sm text-gray-500">Este link não é válido. Fale com o time da Vigor Energy para receber o link de novo.</p>
                    </div>
                ) : (
                    <>
                        <section className="rounded-2xl border border-gray-100 border-t-4 border-t-primary bg-white p-5 shadow-sm">
                            <p className="text-sm text-gray-500">Olá{acordo.primeiro_nome ? `, ${acordo.primeiro_nome}` : ''}</p>
                            <h1 className="mt-1 text-xl font-bold text-gray-900">Seu acordo de pagamento</h1>
                            <p className="mt-1 text-sm text-gray-500">UC {acordo.uc}</p>
                            {acordo.encerrado ? (
                                <p className="mt-4 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm text-gray-600">
                                    Este acordo foi encerrado. Para negociar de novo, fale com o time da Vigor Energy.
                                </p>
                            ) : (
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div className="rounded-xl bg-gray-50 p-3">
                                        <p className="text-xs uppercase tracking-wide text-gray-500">Valor do acordo</p>
                                        <p className="text-lg font-bold text-gray-900">{brl(acordo.valor_total)}</p>
                                    </div>
                                    <div className="rounded-xl bg-gray-50 p-3">
                                        <p className="text-xs uppercase tracking-wide text-gray-500">Pagas</p>
                                        <p className="text-lg font-bold text-gray-900">{pagas} de {acordo.parcelas.length}</p>
                                    </div>
                                </div>
                            )}
                        </section>
                        <ul aria-label="Parcelas do acordo" className="space-y-3">
                            {acordo.parcelas.map(p => <LinhaParcela key={`${p.tipo}-${p.numero}`} p={p} encerrado={acordo.encerrado} />)}
                        </ul>
                        <p className="text-center text-xs text-gray-500 pb-6">
                            Os boletos aparecem aqui conforme são emitidos. Guarde este link.
                        </p>
                    </>
                )}
            </main>
        </div>
    );
};

export default AcordoPublicoView;
